
import { supabase } from "@/integrations/supabase/client";
import { withErrorHandling } from "../utils/errorHandling";

/**
 * Service for generating and managing meal composition tags
 */
export const mealCompositionTagService = {
  /**
   * Generate tags for a meal composition using OpenAI
   */
  generateCompositionTags: withErrorHandling(
    async (compositionName: string, recipeNames: string[]): Promise<string[]> => {
      if (!compositionName || !recipeNames || recipeNames.length === 0) {
        throw new Error("Composition name and recipe names are required for tag generation");
      }

      console.log('Generating tags for composition:', compositionName, 'with recipes:', recipeNames);

      const { data, error } = await supabase.functions.invoke('generate-composition-tags', {
        body: {
          compositionName,
          recipeNames
        }
      });

      if (error) {
        console.error('Error calling generate-composition-tags function:', error);
        throw new Error(`Failed to generate composition tags: ${error.message}`);
      }

      if (!data || !data.tags || !Array.isArray(data.tags)) {
        console.error('Invalid response from generate-composition-tags function:', data);
        throw new Error('Invalid response from tag generation service');
      }

      console.log('Generated composition tags:', data.tags);
      return data.tags;
    },
    "Error generating composition tags"
  ),

  /**
   * Update a composition with generated tags
   */
  updateCompositionTags: withErrorHandling(
    async (compositionId: string, tags: string[]): Promise<void> => {
      console.log('Updating composition tags for:', compositionId, 'with tags:', tags);

      const { error } = await supabase
        .from("meal_compositions")
        .update({ tags })
        .eq("id", compositionId);

      if (error) {
        console.error('Error updating composition tags:', error);
        throw error;
      }

      console.log('Successfully updated composition tags');
    },
    "Error updating composition tags"
  ),

  /**
   * Generate and save tags for a composition
   */
  generateAndSaveCompositionTags: withErrorHandling(
    async (compositionId: string, compositionName: string, recipeNames: string[]): Promise<string[]> => {
      // Generate tags using OpenAI
      const tags = await mealCompositionTagService.generateCompositionTags(compositionName, recipeNames);
      
      // Update the composition with the generated tags
      await mealCompositionTagService.updateCompositionTags(compositionId, tags);
      
      return tags;
    },
    "Error generating and saving composition tags"
  )
};
